import {
  ContactValidationResult,
  IdentityVerificationResult,
  ISavePhoneNumberPayload,
  ICancelRequestPayload,
  ICancelRequestResponse,
} from 'api/file/interfaces/get-file-order-insights';
import { PartyVerificationListResult } from 'features/services/identityVerification/interfaces/partyList';
import {
  WireValidationListResult,
  WireValidationSubmitRequest,
  WireValidationSubmitResponse,
} from 'features/services/wireVerification/interfaces/wireValidation';

import { handleError } from 'utilities/utilities';
import { PartyVerificationDrawerProps } from 'features/services/identityVerification/interfaces/PartyDrawer';
import { getAgencyApiHost, getApiHost } from '../host-by-env';

interface MarketplaceOrderReportResponse {
  Result: {
    FileName: string;
    Data: string;
  };
}

export async function validateContactInfo(
  payload: ISavePhoneNumberPayload,
  isPartyVerification: boolean,
  token: string,
): Promise<ContactValidationResult> {
  const url = `${getApiHost()}marketplace/${
    isPartyVerification ? 'party-verification' : 'services'
  }/contact-validation`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function verifyIdentity(
  payload: ISavePhoneNumberPayload,
  isPartyVerification: boolean,
  token: string,
): Promise<IdentityVerificationResult> {
  const url = `${getApiHost()}marketplace/${
    isPartyVerification ? 'party-verification' : 'services'
  }/identity-verification`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getPartyList(firmId: string | undefined, token: string): Promise<PartyVerificationListResult[]> {
  const url = `${getApiHost()}marketplace/party-verification/list/${firmId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getWireValidationList(
  firmId: string | undefined,
  token: string,
): Promise<WireValidationListResult[]> {
  const url = `${getApiHost()}marketplace/wire-validation/list/${firmId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function submitWireValidation(
  payload: WireValidationSubmitRequest,
  token: string,
): Promise<WireValidationSubmitResponse> {
  const url = `${getApiHost()}marketplace/wire-validation/ad-hoc`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getWireValidationOrderDetail(
  fileId: string,
  orderId: string,
  token: string,
): Promise<WireValidationSubmitResponse> {
  const url = `${getApiHost()}marketplace/wire-validation/ad-hoc/${fileId}/${orderId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getMarketplaceOrderReport(
  fileId: string,
  orderId: string,
  token: string,
  isAdhoc?: boolean,
): Promise<MarketplaceOrderReportResponse> {
  const url = `${getAgencyApiHost()}marketplace/v1/orders/${fileId}/${orderId}/report${isAdhoc ? '?isAdhoc=true' : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });
  const json = await response.json();
  handleError(json);
  return json;
}

export async function getPartyVerificationFileDetail(
  fileId: string,
  token: string,
): Promise<PartyVerificationDrawerProps['party']> {
  const url = `${getApiHost()}marketplace/party-verification/file-detail/${fileId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getAdhocOrderJson(orderId: string, fileId: string, token: string): Promise<unknown> {
  const url = `${getAgencyApiHost()}marketplace/v1/adhoc-orders?orderId=${orderId}&fileId=${fileId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  const json = await response.json();
  handleError(json);
  return json.result ?? json;
}

export async function cancelPartyVerificationRequest(
  payload: ICancelRequestPayload,
  token: string,
): Promise<ICancelRequestResponse> {
  const url = `${getApiHost()}marketplace/party-verification/identity-verification/user-cancel`;
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}
